import { useEffect, useMemo, useState } from "react";
import { useHistory, useLocation } from "react-router-dom";
import Box from "@material-ui/core/Box";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import Table from "@material-ui/core/Table";
import TableBody from "@material-ui/core/TableBody";
import TableCell from "@material-ui/core/TableCell";
import TableContainer from "@material-ui/core/TableContainer";
import TableHead from "@material-ui/core/TableHead";
import TableRow from "@material-ui/core/TableRow";
import Button from "@material-ui/core/Button";
import AddCircle from "@material-ui/icons/AddCircle";
import DesktopWindows from "@material-ui/icons/DesktopWindows";
import { useTotemList } from "../../../services/api-tansquery/totem/list";
import TotemListTableActions from "./components/table-action";
import SearchField from "../components/search-field";
import { useTotemStyles } from "../styles";
import { useTotemListStyles } from "./styles";
import CustomPagination from "../../../Components/pagination";
import { dateformat } from "../../../utils/dateFormat";
import { useBreadcrumbs } from "../../../contexts/breadcrumb";
import EmptyState from "../../../Components/EmptyState";
import { usePermission } from "../../../hooks/usePermission";
import TotemFilters from "../components/filters";
import { FilterList } from "@material-ui/icons";
import { useSearchParams } from "../../../hooks/useSearchParams";
import Grid from "@material-ui/core/Grid";

export default function TotemList() {
    const history = useHistory();
    const searchParams = useSearchParams();
    const params = useMemo(() => {
        return {
            status: searchParams.get("status") || undefined,
            created_at: searchParams.get("created_at") || undefined,
            updated_at: searchParams.get("updated_at") || undefined,
        };
    }, [searchParams]);
    const { setBreadcrumbs } = useBreadcrumbs();
    const { pathname } = useLocation();
    const { isAboveCoordenator } = usePermission();
    const { classes } = useTotemListStyles();
    const { classes: defaultClasses } = useTotemStyles();
    const { data: totemList, isLoading } = useTotemList(params);
    const [filtersOpen, setFiltersOpen] = useState(false);

    const isEmpty = (totemList?.results ?? []).length === 0;

    useEffect(() => {
        setBreadcrumbs((prev) => {
            const homeBreadcrumb = prev.find((_, index) => index === 0);
            return [
                homeBreadcrumb,
                {
                    label: "Totem",
                    path: "",
                    icon: DesktopWindows,
                },
            ];
        });
    }, [setBreadcrumbs]);

    useEffect(() => {
        if (Object.values(params).some(value => value)) {
            setFiltersOpen(true);
        } else {
            setFiltersOpen(false);
        }
    }, [params]);

    return (
        <Box>
            <Box className={classes.header}>
                <Typography className={classes.title}>
                    Totens de Autoatendimento
                </Typography>
                {isAboveCoordenator && (
                    <Button
                        startIcon={<AddCircle />}
                        className={defaultClasses.addButton}
                        onClick={() => history.push(`${pathname}/novo`)}
                    >
                        Novo Totem
                    </Button>
                )}
            </Box>
            <Paper elevation={3}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <Box flexDirection="row" display="flex" alignItems="center" paddingX={2}>
                            <Button
                                startIcon={<FilterList />}
                                variant="contained"
                                onClick={() => setFiltersOpen(!filtersOpen)}
                                style={{
                                    color: filtersOpen ? "#fff" : "#004F9F",
                                    backgroundColor: filtersOpen ? "#004F9F" : "#fff",
                                    border: filtersOpen ? "none" : "1px solid #004F9F",
                                    boxShadow: 'none',
                                }}
                            >
                                Filtros
                            </Button>
                            <SearchField
                                placeholder="Buscar por nome"
                            />
                        </Box>
                    </Grid>
                    <Grid item xs={12}>
                        <TotemFilters open={filtersOpen} />
                    </Grid>
                </Grid>
                <TableContainer className={defaultClasses.tableContainer}>
                    <Table stickyHeader aria-label="sticky table">
                        <TableHead>
                            <TableRow>
                                <TableCell className={defaultClasses.tableTH}>
                                    Órgão
                                </TableCell>
                                <TableCell className={defaultClasses.tableTH}>Totem</TableCell>
                                <TableCell className={defaultClasses.tableTH}>Ativo</TableCell>
                                <TableCell className={defaultClasses.tableTH}>
                                    Adicionado
                                </TableCell>
                                <TableCell className={defaultClasses.tableTH}>
                                    Atualizado
                                </TableCell>
                                <TableCell className={defaultClasses.tableTH} />
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {totemList?.results.map((totem) => (
                                <TableRow key={totem.id} hover>
                                    <TableCell className={defaultClasses.tableCell}>
                                        {totem.orgao?.nome}
                                    </TableCell>
                                    <TableCell className={defaultClasses.tableCell}>
                                        {totem.descricao}
                                    </TableCell>
                                    <TableCell className={defaultClasses.tableCell}>
                                        <Box
                                            className={`${defaultClasses.badge} ${totem.ativo
                                                ? defaultClasses.active
                                                : defaultClasses.inactive
                                                }`}
                                        >
                                            {totem.ativo ? "Sim" : "Não"}
                                        </Box>
                                    </TableCell>
                                    <TableCell className={defaultClasses.tableCell}>
                                        {dateformat(totem.created_at, true)}
                                    </TableCell>
                                    <TableCell className={defaultClasses.tableCell}>
                                        {dateformat(totem.updated_at, true)}
                                    </TableCell>
                                    <TableCell className={defaultClasses.tableCell}>
                                        <TotemListTableActions totem={totem} />
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                    {isEmpty && (
                        <EmptyState
                            loading={isLoading}
                            loadingText="Carregando totens..."
                            message="Nenhum totem encontrado."
                        />
                    )}
                </TableContainer>
                <Box className={defaultClasses.paginationContainer}>
                    <CustomPagination
                        count={totemList?.count}
                        page={totemList?.current}
                        totalPages={totemList?.total_pages}
                    />
                </Box>
            </Paper>
        </Box>
    );
}
